/**
 * DigitalIDCard — AOS-LID-01/03: Campus Digital ID
 *
 * Route: /my-id  (student role only)
 *
 * LID-03 photo states:
 *  - NONE:               "Add your photo" upload prompt
 *  - PENDING_REVIEW:     amber banner — "Photo under review"
 *  - PENDING_REPLACEMENT: approved photo shown + amber banner for replacement
 *  - APPROVED:           photo displayed on card
 *  - REJECTED:           red banner with rejection reason + re-upload option
 *
 * Security:
 *  - QR payload is opaque — no raw PII, no session credentials
 *  - Photo fetched through authenticated endpoint (not a public URL)
 *  - Card data requires campus.id.view.own permission
 *  - Synthetic demo identity displayed with informational label (not blocked in dev)
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  AlertCircle, AlertTriangle, BadgeCheck, Building2,
  Camera, CheckCircle2, Clock, GraduationCap, Home,
  Loader2, RefreshCw, ShieldAlert, ShieldCheck, User, XCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TTL = 300; // must match backend TOKEN_TTL_SECONDS

// ─── Lifecycle status colours ────────────────────────────────────────────────
function lifecycleChip(status) {
  const map = {
    ACTIVE:    { bg: "bg-emerald-500/20", text: "text-emerald-300", dot: "bg-emerald-400" },
    ENROLLED:  { bg: "bg-emerald-500/20", text: "text-emerald-300", dot: "bg-emerald-400" },
    LEAVE:     { bg: "bg-amber-500/20",   text: "text-amber-300",   dot: "bg-amber-400"   },
    SUSPENDED: { bg: "bg-red-500/20",     text: "text-red-300",     dot: "bg-red-400"     },
    WITHDRAWN: { bg: "bg-slate-500/20",   text: "text-slate-400",   dot: "bg-slate-500"   },
    GRADUATED: { bg: "bg-sky-500/20",     text: "text-sky-300",     dot: "bg-sky-400"     },
    ALUMNUS:   { bg: "bg-purple-500/20",  text: "text-purple-300",  dot: "bg-purple-400"  },
  };
  return map[status] || { bg: "bg-slate-700/40", text: "text-slate-400", dot: "bg-slate-500" };
}

// ─── QR Timer ────────────────────────────────────────────────────────────────
function QrTimer({ expiresAt, onExpired }) {
  const [secondsLeft, setSecondsLeft] = useState(TTL);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setSecondsLeft(diff);
      if (diff === 0) onExpired();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpired]);

  const pct = Math.min(100, (secondsLeft / TTL) * 100);
  const color =
    secondsLeft > 120 ? "#10b981" :
    secondsLeft > 60  ? "#f59e0b" : "#ef4444";

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="flex flex-col items-center gap-1 w-full">
      <div className="w-full h-1 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <p
        className="text-xs font-mono font-semibold"
        style={{ color }}
        data-testid="qr-timer"
      >
        {secondsLeft > 0 ? `Valid ${mm}:${ss}` : "Expired — refresh"}
      </p>
    </div>
  );
}

// ─── Photo ────────────────────────────────────────────────────────────────────
function StudentPhoto({ hasPhoto, token }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!hasPhoto || !token) return;
    // Fetch through authenticated proxy
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campus/id/my-card/photo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("no photo");
        return r.blob();
      })
      .then((blob) => setSrc(URL.createObjectURL(blob)))
      .catch(() => setFailed(true));
  }, [hasPhoto, token]);

  const size = "w-20 h-20 sm:w-24 sm:h-24";

  if (src && !failed) {
    return (
      <img
        src={src}
        alt="Student"
        className={`${size} rounded-full object-cover ring-4 ring-white/20 shadow-lg`}
        data-testid="student-photo"
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full bg-white/10 ring-4 ring-white/20 flex items-center justify-center`}
      data-testid="student-photo-placeholder"
    >
      <User className="w-10 h-10 text-white/40" />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DigitalIDCard() {
  const { user, token } = useAuth();

  // Card data state
  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(true);
  const [cardError, setCardError] = useState(null);

  // QR state
  const [qr, setQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [qrExpired, setQrExpired] = useState(false);

  const refreshingRef = useRef(false);

  // ── Fetch card data ────────────────────────────────────────────────────────
  const fetchCard = useCallback(async () => {
    setCardLoading(true);
    setCardError(null);
    try {
      const { data } = await api.get("/campus/id/my-card");
      setCard(data);
    } catch (err) {
      setCardError(err?.response?.data?.detail || "Unable to load your digital ID card.");
    } finally {
      setCardLoading(false);
    }
  }, []);

  // ── Generate QR ────────────────────────────────────────────────────────────
  const generateQr = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setQrLoading(true);
    setQrError(null);
    setQrExpired(false);
    try {
      const { data } = await api.post("/campus/id/my-card/qr", {});
      setQr(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "CARD_SUSPENDED") {
        setQrError("SUSPENDED");
      } else if (typeof detail === "object" && detail?.code === "RATE_LIMITED") {
        setQrError("RATE_LIMITED");
      } else {
        setQrError(typeof detail === "string" ? detail : "Unable to generate QR code.");
      }
    } finally {
      setQrLoading(false);
      refreshingRef.current = false;
    }
  }, []);

  const handleQrExpired = useCallback(() => {
    setQrExpired(true);
  }, []);

  // ── Auto-refresh QR on expiry ─────────────────────────────────────────────
  useEffect(() => {
    if (qrExpired && !qrLoading) {
      generateQr();
    }
  }, [qrExpired, qrLoading, generateQr]);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  // Generate initial QR when card loads (and not suspended)
  useEffect(() => {
    if (card && !card.is_suspended) {
      generateQr();
    }
  }, [card]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading ────────────────────────────────────────────────────────────────
  if (cardLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-sky-400 animate-spin" />
          <p className="text-slate-400 text-sm">Loading your ID card…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (cardError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-slate-900 border border-red-800/50 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-white font-semibold mb-1">ID Card Unavailable</h2>
          <p className="text-slate-400 text-sm mb-4">{
            typeof cardError === "object" && cardError?.message
              ? cardError.message
              : String(cardError)
          }</p>
          <button
            onClick={fetchCard}
            className="bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            data-testid="retry-btn"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!card) return null;

  const c = lifecycleChip(card.lifecycle_status);
  const isActive = ["ACTIVE", "ENROLLED"].includes(card.lifecycle_status);
  const qrString = qr ? JSON.stringify(qr.qr_payload) : null;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col items-center justify-start py-6 px-4"
      data-testid="digital-id-card-page"
    >
      {/* ── Card ─────────────────────────────────────────────────────────── */}
      <div
        className="w-full max-w-sm relative overflow-hidden rounded-3xl shadow-2xl border border-white/10"
        style={{
          background: "linear-gradient(145deg, #0f1e35 0%, #0a1628 60%, #081322 100%)",
        }}
        data-testid="id-card"
      >
        {/* Top stripe */}
        <div className="h-1.5 w-full bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500" />

        {/* ── Header: Institution ────────────────────────────────────────── */}
        <div className="px-5 pt-4 pb-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-sky-600/30 border border-sky-500/30 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-sky-400" />
          </div>
          <div className="min-w-0">
            <p
              className="text-white font-bold text-sm leading-tight truncate"
              data-testid="institution-name"
            >
              {card.institution_name || "Your Institution"}
            </p>
            <p className="text-sky-400/70 text-[10px] font-semibold tracking-widest uppercase">
              Campus Digital ID
            </p>
          </div>
          <div className="ml-auto">
            {card.is_suspended ? (
              <ShieldAlert className="w-6 h-6 text-red-400" data-testid="suspended-icon" />
            ) : (
              <ShieldCheck className="w-6 h-6 text-emerald-400" data-testid="verified-icon" />
            )}
          </div>
        </div>

        <div className="mx-5 border-t border-white/5" />

        {/* ── Identity section ──────────────────────────────────────────── */}
        <div className="px-5 py-4 flex items-center gap-4">
          <StudentPhoto hasPhoto={card.has_photo} token={token} />
          <div className="flex-1 min-w-0">
            <h1
              className="text-white font-bold text-lg leading-tight truncate"
              data-testid="student-display-name"
            >
              {card.display_name || `${card.given_name} ${card.family_name}`}
            </h1>
            <p
              className="text-slate-400 text-xs font-mono mt-0.5"
              data-testid="student-number"
            >
              {card.student_number}
            </p>

            {/* Enrolment status chip */}
            <div className="mt-2 flex items-center gap-1.5">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${c.bg} ${c.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                {card.lifecycle_status}
              </span>
            </div>
          </div>
        </div>

        {/* ── Programme & Campus ────────────────────────────────────────── */}
        <div className="mx-5 grid grid-cols-1 gap-2 pb-3">
          {card.enrollment ? (
            <>
              <div className="flex items-start gap-2">
                <GraduationCap className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-white text-xs font-semibold leading-tight" data-testid="program-name">
                    {card.enrollment.program_name || "Programme"}
                  </p>
                  <p className="text-slate-500 text-[10px]">
                    {card.enrollment.degree_level && `${card.enrollment.degree_level} · `}
                    {card.enrollment.campus_name || "Main Campus"}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <GraduationCap className="w-3.5 h-3.5 text-slate-600 shrink-0" />
              <p className="text-slate-600 text-xs">Enrolment not yet linked</p>
            </div>
          )}

          {/* Hostel entitlement */}
          {card.hostel ? (
            <div className="flex items-start gap-2" data-testid="hostel-badge">
              <Home className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-emerald-300 text-xs font-semibold leading-tight">
                  {card.hostel.hostel_name}
                </p>
                <p className="text-slate-500 text-[10px]">
                  Room {card.hostel.room_number}
                  {card.hostel.bed_label ? ` · ${card.hostel.bed_label}` : ""}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2" data-testid="no-hostel-badge">
              <Home className="w-3.5 h-3.5 text-slate-700 shrink-0" />
              <p className="text-slate-600 text-[10px]">No active hostel allocation</p>
            </div>
          )}
        </div>

        <div className="mx-5 border-t border-white/5" />

        {/* ── QR Section ───────────────────────────────────────────────── */}
        <div className="px-5 py-4 flex flex-col items-center gap-3">
          {card.is_suspended ? (
            <SuspendedState reason={card.suspension?.reason} />
          ) : !isActive ? (
            <InactiveState status={card.lifecycle_status} />
          ) : qrLoading ? (
            <QrLoadingState />
          ) : qrError === "RATE_LIMITED" ? (
            <RateLimitedState onRetry={() => setTimeout(generateQr, 3000)} />
          ) : qrError ? (
            <QrErrorState error={qrError} onRetry={generateQr} />
          ) : qrString ? (
            <>
              <div
                className="bg-white rounded-xl p-2.5 shadow-lg"
                data-testid="qr-code-container"
              >
                <QRCodeCanvas
                  value={qrString}
                  size={180}
                  level="M"
                  imageSettings={{
                    src: "",
                    excavate: false,
                  }}
                  data-testid="qr-canvas"
                />
              </div>
              <QrTimer expiresAt={qr.expires_at} onExpired={handleQrExpired} />
              <button
                onClick={generateQr}
                disabled={qrLoading}
                className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 transition-colors disabled:opacity-50"
                data-testid="refresh-qr-btn"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh QR
              </button>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 flex items-center justify-between">
          <p className="text-slate-600 text-[9px] font-mono">AOS·LID·01</p>
          <p className="text-slate-600 text-[9px]">
            {card.enrollment?.program_code || "G9 AcademiaOS"}
          </p>
          <BadgeCheck className="w-3.5 h-3.5 text-sky-500/50" />
        </div>

        {/* Bottom stripe */}
        <div className="h-0.5 w-full bg-gradient-to-r from-sky-500/30 via-blue-500/30 to-indigo-500/30" />
      </div>

      {/* ── Photo upload prompt ─────────────────────────────────────────── */}
      {card.photo_status === "NONE" && (
        <PhotoUploadPrompt token={token} onUploaded={fetchCard} />
      )}

      {/* ── Photo status panel (LID-03) ─────────────────────────────────── */}
      <PhotoStatusPanel card={card} token={token} onUploaded={fetchCard} />

      {/* ── Synthetic identity notice ───────────────────────────────────── */}
      {card.is_synthetic && (
        <div className="mt-3 w-full max-w-sm bg-amber-950/20 border border-amber-700/30 rounded-xl px-4 py-2 flex items-center gap-2" data-testid="synthetic-notice">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <p className="text-amber-500/80 text-[10px]">Demo pilot identity — for testing only</p>
        </div>
      )}

      {/* ── Admission info ──────────────────────────────────────────────── */}
      {card.admission_date && (
        <p className="mt-4 text-slate-600 text-xs">
          Admitted: {new Date(card.admission_date).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
          })}
        </p>
      )}
    </div>
  );
}

// ─── QR sub-states ────────────────────────────────────────────────────────────
function QrLoadingState() {
  return (
    <div
      className="w-[196px] h-[196px] bg-white/5 rounded-xl flex flex-col items-center justify-center gap-2"
      data-testid="qr-loading"
    >
      <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      <p className="text-slate-500 text-xs">Generating QR…</p>
    </div>
  );
}

function QrErrorState({ error, onRetry }) {
  return (
    <div
      className="w-full bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex flex-col items-center gap-2"
      data-testid="qr-error"
    >
      <AlertCircle className="w-7 h-7 text-red-400" />
      <p className="text-red-300 text-xs text-center">{error}</p>
      <button
        onClick={onRetry}
        className="mt-1 text-xs text-sky-400 hover:text-sky-300 underline"
        data-testid="qr-retry-btn"
      >
        Try again
      </button>
    </div>
  );
}

function RateLimitedState({ onRetry }) {
  return (
    <div className="w-full bg-amber-950/30 border border-amber-700/40 rounded-xl p-4 flex flex-col items-center gap-2" data-testid="qr-rate-limited">
      <AlertTriangle className="w-7 h-7 text-amber-400" />
      <p className="text-amber-300 text-xs text-center">Too many requests. Please wait a moment.</p>
      <button onClick={onRetry} className="text-xs text-sky-400 hover:text-sky-300 underline">Retry</button>
    </div>
  );
}

function SuspendedState({ reason }) {
  return (
    <div
      className="w-full bg-red-950/40 border border-red-700/50 rounded-xl p-4 flex flex-col items-center gap-2"
      data-testid="suspended-state"
    >
      <ShieldAlert className="w-10 h-10 text-red-400" />
      <p className="text-red-300 font-semibold text-sm">Card Suspended</p>
      {reason && <p className="text-red-400/70 text-xs text-center">{reason}</p>}
      <p className="text-slate-500 text-xs text-center mt-1">
        Contact your institution administrator.
      </p>
    </div>
  );
}

function InactiveState({ status }) {
  return (
    <div
      className="w-full bg-slate-800/50 border border-slate-700/40 rounded-xl p-4 flex flex-col items-center gap-2"
      data-testid="inactive-state"
    >
      <AlertTriangle className="w-8 h-8 text-amber-400" />
      <p className="text-amber-300 font-semibold text-sm">QR Unavailable</p>
      <p className="text-slate-400 text-xs text-center">
        Your enrolment status is <strong className="text-amber-300">{status}</strong>.
        QR verification is available only for active students.
      </p>
    </div>
  );
}

// ─── Photo Upload Prompt ──────────────────────────────────────────────────────
function PhotoUploadPrompt({ token, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campus/id/my-card/photo`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form },
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        const detail = d.detail;
        if (typeof detail === "object" && detail?.code === "PHOTO_REVIEW_PENDING") {
          throw new Error("A photo submission is already pending review.");
        }
        throw new Error(typeof detail === "string" ? detail : "Upload failed");
      }
      setDone(true);
      onUploaded();
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (done) return null;

  return (
    <div
      className="mt-4 w-full max-w-sm bg-sky-950/30 border border-sky-800/40 rounded-2xl p-4 flex flex-col items-center gap-2"
      data-testid="photo-upload-prompt"
    >
      <Camera className="w-6 h-6 text-sky-400" />
      <p className="text-sky-300 text-xs font-semibold">Add your photo</p>
      <p className="text-slate-500 text-[10px] text-center">
        JPG, PNG or WebP · min 150×150 px · max 5 MB
      </p>
      {error && <p className="text-red-400 text-xs text-center">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFile}
        data-testid="photo-file-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-medium px-4 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
        data-testid="upload-photo-btn"
      >
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
        {uploading ? "Uploading…" : "Choose Photo"}
      </button>
    </div>
  );
}

// ─── Photo Status Panel (LID-03) ──────────────────────────────────────────────
/**
 * Shows the current photo review state to the student.
 *
 * Handled states: PENDING_REVIEW, PENDING_REPLACEMENT, REJECTED, REMOVED
 * Returns null for APPROVED (photo shows on card) and NONE (upload prompt shown separately).
 */
function PhotoStatusPanel({ card, token, onUploaded }) {
  const [withdrawing, setWithdrawing] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campus/id/my-card/photo/withdraw`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason: "Withdrawn by student" }),
        }
      );
      onUploaded();
    } catch (e) {
      // non-fatal
    } finally {
      setWithdrawing(false);
    }
  };

  const handleReupload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campus/id/my-card/photo`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form },
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        const detail = d.detail;
        if (typeof detail === "object" && detail?.code === "PHOTO_REVIEW_PENDING") {
          throw new Error("A submission is already pending review.");
        }
        throw new Error(typeof detail === "string" ? detail : "Upload failed");
      }
      onUploaded();
    } catch (err) {
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const status = card.photo_status;

  // These states are handled elsewhere — no additional banner needed
  if (status === "APPROVED" || status === "NONE") return null;

  if (status === "PENDING_REVIEW") {
    return (
      <div
        className="mt-4 w-full max-w-sm bg-amber-950/20 border border-amber-700/30 rounded-2xl p-4 flex flex-col items-center gap-2"
        data-testid="photo-pending-banner"
      >
        <Clock className="w-5 h-5 text-amber-400" />
        <p className="text-amber-300 text-xs font-semibold">Photo under review</p>
        <p className="text-slate-500 text-[10px] text-center">
          Your photo submission is awaiting staff review. This usually takes 1–2 business days.
        </p>
        <button
          onClick={handleWithdraw}
          disabled={withdrawing}
          className="mt-1 text-[10px] text-slate-500 hover:text-red-400 transition-colors flex items-center gap-1 disabled:opacity-50"
          data-testid="withdraw-photo-btn"
        >
          {withdrawing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
          Withdraw submission
        </button>
      </div>
    );
  }

  if (status === "PENDING_REPLACEMENT") {
    return (
      <div
        className="mt-4 w-full max-w-sm bg-amber-950/20 border border-amber-700/30 rounded-2xl p-4 flex flex-col items-center gap-2"
        data-testid="photo-pending-replacement-banner"
      >
        <Clock className="w-5 h-5 text-amber-400" />
        <p className="text-amber-300 text-xs font-semibold">Replacement photo under review</p>
        <p className="text-slate-500 text-[10px] text-center">
          Your current photo remains active. A replacement is pending staff approval.
        </p>
        <button
          onClick={handleWithdraw}
          disabled={withdrawing}
          className="mt-1 text-[10px] text-slate-500 hover:text-red-400 transition-colors flex items-center gap-1 disabled:opacity-50"
          data-testid="withdraw-replacement-btn"
        >
          {withdrawing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
          Withdraw replacement
        </button>
      </div>
    );
  }

  if (status === "REJECTED") {
    return (
      <div
        className="mt-4 w-full max-w-sm bg-red-950/30 border border-red-800/40 rounded-2xl p-4 flex flex-col items-center gap-2"
        data-testid="photo-rejected-banner"
      >
        <XCircle className="w-5 h-5 text-red-400" />
        <p className="text-red-300 text-xs font-semibold">Photo Rejected</p>
        {card.photo_rejection_reason && (
          <p className="text-red-400/70 text-[10px] text-center">
            Reason: {card.photo_rejection_reason}
          </p>
        )}
        <p className="text-slate-500 text-[10px] text-center">
          Please upload a new photo meeting the requirements.
        </p>
        {uploadError && <p className="text-red-400 text-[10px]">{uploadError}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleReupload}
          data-testid="reupload-file-input"
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="mt-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-[10px] font-medium px-4 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
          data-testid="reupload-photo-btn"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          {uploading ? "Uploading…" : "Upload new photo"}
        </button>
      </div>
    );
  }

  if (status === "REMOVED") {
    return (
      <div
        className="mt-4 w-full max-w-sm bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4 flex flex-col items-center gap-2"
        data-testid="photo-removed-banner"
      >
        <User className="w-5 h-5 text-slate-500" />
        <p className="text-slate-400 text-xs font-semibold">Photo Removed</p>
        <p className="text-slate-600 text-[10px] text-center">
          Your profile photo was removed by an administrator. Please upload a new photo.
        </p>
        {uploadError && <p className="text-red-400 text-[10px]">{uploadError}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleReupload}
          data-testid="removed-reupload-file-input"
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="mt-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-[10px] font-medium px-4 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
          data-testid="removed-reupload-btn"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          {uploading ? "Uploading…" : "Upload new photo"}
        </button>
      </div>
    );
  }

  return null;
}
